import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

// Hooks
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { setIsOpen, setSelectedPromo } from "@/features/journey/journeySlice";

// Types
import { AppLocation } from "@/types/enums";
import { RootState } from "@/store";

// Context
import { UnitItemContext } from "@/context/UnitItemContext";

// Components
import { JourneyContent } from "@/components/journey/JourneyContent";
import { JourneyHeader } from "@/components/journey/layout/JourneyHeader";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

export const UnitJourneyWrapper = () => {
    const { rentalApp } = useDisplayOptionContext();
    const dispatch = useDispatch();

    const { isOpen, selectedUnitGroup, selectedPromo } = useSelector(
        (state: RootState) => ({
            isOpen: state.journey.isOpen,
            selectedUnitGroup: state.journey.selectedUnitGroup,
            selectedPromo: state.journey.selectedPromo,
        }),
        shallowEqual,
    );

    // No useCallback needed - The Compiler handles function stability
    const onOpenChange = (value: boolean) => {
        dispatch(setIsOpen(value));
    };

    // Early return prevents crash if data isn't ready
    if (!isOpen || !selectedUnitGroup) return null;

    const unitItemContextValue = {
        unitGroup: selectedUnitGroup,
        selectedPromo: selectedPromo,
        setSelectedPromo: (value: number) => dispatch(setSelectedPromo(value)),
    };

    const isDrawer = rentalApp?.location === AppLocation.DRAWER;

    return (
        <UnitItemContext.Provider value={unitItemContextValue}>
            {isDrawer ? (
                <Sheet open={isOpen} onOpenChange={onOpenChange}>
                    <SheetContent
                        className="se-vapor se-vapor-drawer tw:flex-col tw:max-h-screen tw:w-[390px] tw:px-0 tw:z-max no-scrollbar"
                        onInteractOutside={(e) => e.preventDefault()}
                    >
                        <SheetHeader>
                            <VisuallyHidden>
                                <SheetTitle>Unit Details</SheetTitle>
                            </VisuallyHidden>
                            <JourneyHeader />
                        </SheetHeader>
                        <JourneyContent />
                    </SheetContent>
                </Sheet>
            ) : (
                <Dialog open={isOpen} onOpenChange={onOpenChange}>
                    <DialogContent className="se-vapor se-vapor-dialog tw:flex-col tw:z-max">
                        <DialogHeader>
                            <VisuallyHidden>
                                <DialogTitle>Unit Details</DialogTitle>
                            </VisuallyHidden>
                        </DialogHeader>
                        <JourneyContent />
                        <DialogFooter />
                    </DialogContent>
                </Dialog>
            )}
        </UnitItemContext.Provider>
    );
};
